import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { registerClient, registerLawyer } from '../../services/api';

const INITIAL_STATE = {
  // Client Registration Form
  clientForm: {
    data: {
      nombre: '',
      apellidopaterno: '',
      apellidomaterno: '',
      correo: '',
      telefono: '',
      password: '',
      confirmPassword: '',
      personaMoral: '',
      isMoral: false,
      ocupation: '',
      fechaNacimiento: '',
      gender: '',
      formaConocernos: '',
    },
    touchedFields: {},
    errors: {},
    isValid: false,
    isDirty: false,
    isSubmitting: false,
    submitError: null,
    submitSuccess: false,
  },

  // Lawyer Registration Form
  lawyerForm: {
    data: {
      nombre: '',
      apellidopaterno: '',
      apellidomaterno: '',
      correo: '',
      location: {
        country: '',
        state: '',
        city: '',
      },
      fotografia: '',
      telefono: '',
      phoneData: {
        dial_code: '52',
        iso2: 'mx',
        country: 'Mexico',
      },
      password: '',
      confirmPassword: '',
      descripcion: '',
      despacho: '',
      posicion: '',
      ubicacionDespacho: '',
      cedula: '',
      fechaInicio: '',
      fechaNacimiento: '',
      educacion: {
        licenciatura: '',
        maestria: '',
        doctorado: '',
        diplomado: '',
      },
      idiomas: [],
      servicios: [],
      especialidades: [],
      pricing: {
        priceRange: '',
        hasFirstConsultation: false,
        firstConsultationPrice: null,
      },
      rfc: '',
    },
    touchedFields: {},
    errors: {},
    isValid: false,
    isDirty: false,
    isSubmitting: false,
    submitError: null,
    submitSuccess: false,
  },

  // Login Form
  loginForm: {
    data: {
      username: '',
      password: '',
    },
    touchedFields: {},
    errors: {},
    isValid: false,
    isDirty: false,
    isSubmitting: false,
    submitError: null,
  },

  // Form Metadata
  formMetadata: {
    lastSaved: null,
    autoSaveEnabled: true,
    currentFormType: null, // 'client' | 'lawyer' | 'login'
  },
};

// Async thunks for form submissions
export const submitClientForm = createAsyncThunk(
  'forms/submitClientForm',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await registerClient(formData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Error en el registro');
    }
  }
);

export const submitLawyerForm = createAsyncThunk(
  'forms/submitLawyerForm',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await registerLawyer(formData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Error en el registro');
    }
  }
);

const formsSlice = createSlice({
  name: 'forms',
  initialState: INITIAL_STATE,
  reducers: {
    // Client Form Actions
    updateClientFormField: (state, action) => {
      const { field, value } = action.payload;

      // Manejar campos anidados (ej: 'phoneData.dial_code')
      if (field.includes('.')) {
        const keys = field.split('.');
        let current = state.clientForm.data;
        for (let i = 0; i < keys.length - 1; i++) {
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
      } else {
        state.clientForm.data[field] = value;
      }

      state.clientForm.isDirty = true;
      state.clientForm.touchedFields[field] = true;

      // Clear field error when user starts typing
      if (state.clientForm.errors[field]) {
        delete state.clientForm.errors[field];
      }
    },

    setClientFormTouched: (state, action) => {
      const { field, touched = true } = action.payload;
      state.clientForm.touchedFields[field] = touched;
    },

    setClientFormError: (state, action) => {
      const { field, error } = action.payload;
      if (error) {
        state.clientForm.errors[field] = error;
      } else {
        delete state.clientForm.errors[field];
      }
    },

    setClientFormErrors: (state, action) => {
      state.clientForm.errors = action.payload;
    },

    clearClientForm: state => {
      state.clientForm = INITIAL_STATE.clientForm;
    },

    // Lawyer Form Actions
    updateLawyerFormField: (state, action) => {
      const { field, value } = action.payload;

      // Manejar campos anidados
      if (field.includes('.')) {
        const keys = field.split('.');
        let current = state.lawyerForm.data;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
      } else {
        state.lawyerForm.data[field] = value;
      }

      state.lawyerForm.isDirty = true;
      state.lawyerForm.touchedFields[field] = true;

      // Clear field error when user starts typing
      if (state.lawyerForm.errors[field]) {
        delete state.lawyerForm.errors[field];
      }
    },

    updateLawyerFormArray: (state, action) => {
      const { field, value } = action.payload;
      state.lawyerForm.data[field] = value;
      state.lawyerForm.isDirty = true;
      state.lawyerForm.touchedFields[field] = true;
    },

    setLawyerFormTouched: (state, action) => {
      const { field, touched = true } = action.payload;
      state.lawyerForm.touchedFields[field] = touched;
    },

    setLawyerFormError: (state, action) => {
      const { field, error } = action.payload;
      if (error) {
        state.lawyerForm.errors[field] = error;
      } else {
        delete state.lawyerForm.errors[field];
      }
    },

    setLawyerFormErrors: (state, action) => {
      state.lawyerForm.errors = action.payload;
    },

    clearLawyerForm: state => {
      state.lawyerForm = INITIAL_STATE.lawyerForm;
    },

    // Login Form Actions
    updateLoginFormField: (state, action) => {
      const { field, value } = action.payload;
      state.loginForm.data[field] = value;
      state.loginForm.isDirty = true;
      state.loginForm.touchedFields[field] = true;

      // Clear field error when user starts typing
      if (state.loginForm.errors[field]) {
        delete state.loginForm.errors[field];
      }
    },

    setLoginFormError: (state, action) => {
      const { field, error } = action.payload;
      if (error) {
        state.loginForm.errors[field] = error;
      } else {
        delete state.loginForm.errors[field];
      }
    },

    clearLoginForm: state => {
      state.loginForm = INITIAL_STATE.loginForm;
    },

    // General Form Actions
    setCurrentFormType: (state, action) => {
      state.formMetadata.currentFormType = action.payload;
    },

    setAutoSave: (state, action) => {
      state.formMetadata.autoSaveEnabled = action.payload;
    },

    markFormAsSaved: (state, action) => {
      const formType = action.payload;
      state.formMetadata.lastSaved = new Date().toISOString();

      if (formType === 'client') {
        state.clientForm.isDirty = false;
      } else if (formType === 'lawyer') {
        state.lawyerForm.isDirty = false;
      }
    },

    // Reset all forms
    clearAllForms: state => {
      return INITIAL_STATE;
    },

    // Bulk actions for validation
    validateClientForm: state => {
      const { data } = state.clientForm;
      const errors = {};

      // Required fields validation
      const requiredFields = [
        'nombre',
        'apellidopaterno',
        'apellidomaterno',
        'correo',
        'telefono',
        'password',
        'confirmPassword',
        'fechaNacimiento',
        'gender',
        'ocupation',
        'formaConocernos',
      ];

      requiredFields.forEach(field => {
        if (!data[field] || data[field].trim() === '') {
          errors[field] = 'Este campo es requerido';
        }
      });

      // Password validation
      if (data.password && data.password.length < 6) {
        errors.password = 'La contraseña debe tener al menos 6 caracteres';
      }

      if (data.password !== data.confirmPassword) {
        errors.confirmPassword = 'Las contraseñas no coinciden';
      }

      // Email validation
      if (data.correo && !/\S+@\S+\.\S+/.test(data.correo)) {
        errors.correo = 'Email inválido';
      }

      // Moral person validation
      if (data.isMoral && !data.personaMoral) {
        errors.personaMoral = 'Nombre de persona moral es requerido';
      }

      state.clientForm.errors = errors;
      state.clientForm.isValid = Object.keys(errors).length === 0;
    },

    validateLawyerForm: state => {
      const { data } = state.lawyerForm;
      const errors = {};

      // Required fields validation
      const requiredFields = [
        'nombre',
        'apellidopaterno',
        'correo',
        'telefono',
        'password',
        'confirmPassword',
        'descripcion',
        'despacho',
        'posicion',
        'ubicacionDespacho',
        'cedula',
        'fechaInicio',
        'fechaNacimiento',
        'rfc',
      ];

      requiredFields.forEach(field => {
        if (!data[field] || data[field].toString().trim() === '') {
          errors[field] = 'Este campo es requerido';
        }
      });

      // Password validation
      if (data.password && data.password.length < 6) {
        errors.password = 'La contraseña debe tener al menos 6 caracteres';
      }

      if (data.password !== data.confirmPassword) {
        errors.confirmPassword = 'Las contraseñas no coinciden';
      }

      // Email validation
      if (data.correo && !/\S+@\S+\.\S+/.test(data.correo)) {
        errors.correo = 'Email inválido';
      }

      // Specialties validation
      if (data.especialidades.length === 0) {
        errors.especialidades = 'Debe seleccionar al menos una especialidad';
      }

      // Price range validation
      if (!data.pricing.priceRange) {
        errors.priceRange = 'Debe seleccionar un rango de precio';
      }

      state.lawyerForm.errors = errors;
      state.lawyerForm.isValid = Object.keys(errors).length === 0;
    },
  },

  extraReducers: builder => {
    // Client Form Submission
    builder
      .addCase(submitClientForm.pending, state => {
        state.clientForm.isSubmitting = true;
        state.clientForm.submitError = null;
        state.clientForm.submitSuccess = false;
      })
      .addCase(submitClientForm.fulfilled, (state, action) => {
        state.clientForm.isSubmitting = false;
        state.clientForm.submitSuccess = true;
        state.clientForm.isDirty = false;
        state.formMetadata.lastSaved = new Date().toISOString();
      })
      .addCase(submitClientForm.rejected, (state, action) => {
        state.clientForm.isSubmitting = false;
        state.clientForm.submitError = action.payload;
        state.clientForm.submitSuccess = false;
      });

    // Lawyer Form Submission
    builder
      .addCase(submitLawyerForm.pending, state => {
        state.lawyerForm.isSubmitting = true;
        state.lawyerForm.submitError = null;
        state.lawyerForm.submitSuccess = false;
      })
      .addCase(submitLawyerForm.fulfilled, (state, action) => {
        state.lawyerForm.isSubmitting = false;
        state.lawyerForm.submitSuccess = true;
        state.lawyerForm.isDirty = false;
        state.formMetadata.lastSaved = new Date().toISOString();
      })
      .addCase(submitLawyerForm.rejected, (state, action) => {
        state.lawyerForm.isSubmitting = false;
        state.lawyerForm.submitError = action.payload;
        state.lawyerForm.submitSuccess = false;
      });
  },
});

// Export actions
export const {
  // Client Form
  updateClientFormField,
  setClientFormTouched,
  setClientFormError,
  setClientFormErrors,
  clearClientForm,
  validateClientForm,

  // Lawyer Form
  updateLawyerFormField,
  updateLawyerFormArray,
  setLawyerFormTouched,
  setLawyerFormError,
  setLawyerFormErrors,
  clearLawyerForm,
  validateLawyerForm,

  // Login Form
  updateLoginFormField,
  setLoginFormError,
  clearLoginForm,

  // General
  setCurrentFormType,
  setAutoSave,
  markFormAsSaved,
  clearAllForms,
} = formsSlice.actions;

// Selectors
export const selectForms = state => state.forms;
export const selectClientForm = state => state.forms.clientForm;
export const selectLawyerForm = state => state.forms.lawyerForm;
export const selectLoginForm = state => state.forms.loginForm;
export const selectFormMetadata = state => state.forms.formMetadata;

// Computed selectors
export const selectClientFormIsValid = state => {
  const form = state.forms.clientForm;
  return Object.keys(form.errors).length === 0 && form.isDirty;
};

export const selectLawyerFormIsValid = state => {
  const form = state.forms.lawyerForm;
  return Object.keys(form.errors).length === 0 && form.isDirty;
};

export const selectHasUnsavedChanges = state => {
  return state.forms.clientForm.isDirty || state.forms.lawyerForm.isDirty;
};

export const selectCurrentFormData = state => {
  const currentType = state.forms.formMetadata.currentFormType;
  if (currentType === 'client') return state.forms.clientForm.data;
  if (currentType === 'lawyer') return state.forms.lawyerForm.data;
  if (currentType === 'login') return state.forms.loginForm.data;
  return null;
};

export default formsSlice.reducer;
